/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IProcessedStyleSet } from '@fluentui/merge-styles/lib/IStyleSet';
import { IButtonProps, IButtonStyles, IconButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { IMessageBarStyleProps, IMessageBarStyles, MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import { useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzurePermissionsResultSwagger } from 'src/core/services/azure/azureSwaggers';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ModalFooter } from 'src/customText/verticals/projectCreationModal/components/ModalFooter';
import { projectCreationContentLeftMargin, ProjectCreationSteps } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import styled from 'styled-components';

export type ProjectCreationConnectStorageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    isExtractionProjects: boolean;
};

const messages = defineMessages({
    title: {
        id: 'customText.connectStorageScreen.title',
        defaultMessage: 'Connect storage'
    },
    description: {
        id: 'customText.connectStorageScreen.description',
        defaultMessage: 'Connect the storage account which contains the documents you wish to use with this project.'
    },
    storageDescription: {
        id: 'customText.connectStorageScreen.storageDescription',
        defaultMessage: `Select a storage account to be connected to the Azure resource and click “Connect storage account” button.
        This step is irreversible, once a storage account is connected you can't disconnect it.`
    },
    storageAccountLabel: {
        id: 'customText.connectStorageScreen.storageAccountLabel',
        defaultMessage: 'Storage account'
    },
    storageAccountTooltipContent: {
        id: 'customText.connectStorageScreen.storageAccountTooltipContent',
        defaultMessage: 'This is a list of storage accounts accessible to you.'
    },
    storageAccountPlaceholder: {
        id: 'customText.connectStorageScreen.storageAccountPlaceholder',
        defaultMessage: 'Choose a storage account'
    },
    refreshDescription: {
        id: 'customText.connectStorageScreen.refreshDescription',
        defaultMessage: `If you've made recent changes to your azure resource, you can click refresh here to make Custom Text
         aware of the changes`
    },
    learnMore: {
        id: 'customText.connectStorageScreen.learnMore',
        defaultMessage: 'Learn more about preparing your storage.'
    },
    requiredRolesDetails: {
        id: 'customText.connectStorageScreen.requiredRolesDetails',
        defaultMessage: 'More details about the required roles'
    },
    enableCORSFailed: {
        id: 'customText.connectStorageScreen.enableCORSFailed',
        defaultMessage: `Make sure that your Text Analytics resource has contributor/owner
and you have the storage blob data contributor roles on the storage account "{storageAccountName}". Also make sure
that you have contributor/owner over the Text Analytics resource "{resourceName}" to complete the connection step successfully.`
    },
    rolesAssignmentWarning: {
        id: 'customText.connectStorageScreen.rolesAssignmentWarning',
        defaultMessage: 'Note: The roles assignment might take up to five minutes to reflect in the portal.'
    },
    azureResourceLabel: {
        id: 'customText.connectStorageScreen.azureResourceLabel',
        defaultMessage: 'Azure resource'
    },
    changeAzureResource: {
        id: 'customText.connectStorageScreen.changeAzureResource',
        defaultMessage: 'Change Azure resource'
    },
    azureResourcePlaceholder: {
        id: 'customText.connectStorageScreen.azureResourcePlaceholder',
        defaultMessage: 'No item selected'
    },
    invalidResource: {
        id: 'customText.connectStorageScreen.invalidResource',
        defaultMessage: 'You must enable managed identity to connect a storage account.'
    },
    invalidRegion: {
        id: 'customText.connectStorageScreen.invalidRegion',
        defaultMessage: 'The region or SKU of the selected resource is not supported, please select another azure resource.'
    },
    enableIdentity: {
        id: 'customText.connectStorageScreen.enableIdentity',
        defaultMessage: 'Enable managed identity.'
    },
    subscription: {
        id: 'customText.connectStorageScreen.subscription',
        defaultMessage: 'Subscription:'
    },
    emptyItemPlaceholder: {
        id: 'customText.connectStorageScreen.emptyItemPlaceholder',
        defaultMessage: 'No items found.'
    },
    enableCORSMessage: {
        id: 'customText.connectStorageScreen.enableCORSMessage',
        defaultMessage: 'Checking the CORS policies, this might take few seconds.'
    },
    connectStorageAccountButtonLabel: {
        id: 'customText.connectStorageScreen.connectStorageAccountButtonLabel',
        defaultMessage: 'Connect storage account'
    },
    storageAccountConnectedButtonLabel: {
        id: 'customText.connectStorageScreen.storageAccountConnectedButtonLabel',
        defaultMessage: 'Storage account connected'
    },
    connectStorageAccountSuccessMessage: {
        id: 'customText.connectStorageScreen.connectStorageAccountSuccessMessage',
        defaultMessage: 'Storage account connected'
    },
    storageAlreadyAttachedMessage: {
        id: 'customText.connectStorageScreen.storageAlreadyAttachedMessage',
        defaultMessage: `You already have a storage account connected to this Azure resource. Each resource can only have
        one connected storage account. To use a different storage account, select a different resource.`
    },
    csAccountPermissionsLabel: {
        id: 'customText.connectStorageScreen.csAccountPermissionsLabel',
        defaultMessage: 'Language Resource required roles'
    },
    storageAccountPermissionsLabel: {
        id: 'customText.connectStorageScreen.storageAccountPermissionsLabel',
        defaultMessage: 'Storage Account required roles'
    },
    ownerContributorRoleGrantedMessage: {
        id: 'customText.connectStorageScreen.ownerContributorRoleGrantedMessage',
        defaultMessage: 'Owner or contributor role is granted'
    },
    ownerContributorRoleRequiredMessage: {
        id: 'customText.connectStorageScreen.ownerContributorRoleRequiredMessage',
        defaultMessage: 'Owner or contributor role is required'
    },
    storageBlobRolesGrantedMessage: {
        id: 'customText.connectStorageScreen.storageBlobRolesGrantedMessage',
        defaultMessage: 'Storage blob data contributor role will be added'
    },
    storageBlobRolesRequiredMessage: {
        id: 'customText.connectStorageScreen.storageBlobRolesRequiredMessage',
        defaultMessage: 'Storage blob data owner or storage blob data contributor role is required'
    }
});

const stepTheme = getScopedTheme('ProjectCreationConnectStorage');
const refreshButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    iconDisabled: { color: stepTheme.disableColor },
    rootPressed: { backgroundColor: 'transparent' },
    rootHovered: { backgroundColor: 'transparent' },
    rootDisabled: { backgroundColor: 'transparent' },
    root: { marginTop: '30px' }
});
const connectStorageButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { marginTop: '30px' }
});
const connectStorageMessageBarStyles = classNamesFunction<IMessageBarStyleProps, IMessageBarStyles>()({
    root: { marginTop: '-28px' }
});
const successIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '16px', color: stepTheme.successColor }
});
const pendingIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '16px', color: stepTheme.pendingColor }
});
const warningIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '16px', color: stepTheme.warningColor }
});

const StyledDropdown = styled(LuisDropdown)<{ isLargeOrGreater: boolean }>(props => ({
    width: props.isLargeOrGreater ? '300px' : '100%'
}));

export const ConnectStorageScreen = observer((props: ProjectCreationConnectStorageProps) => {
    const { onSubmit, onDismiss, isExtractionProjects } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { csAccountStore, subscriptionId } = rootStore.azureStore.session;
    const { subscriptionStoreHouse } = rootStore.azureStore;
    const subscriptionName = subscriptionStoreHouse.findById(subscriptionId)?.name ?? '-';

    const [storageAccountName, setStorageAccountName] = React.useState(csAccountStore?.ownedStorageAccountInfo.name ?? '');

    const { isLargeOrGreater } = useResponsiveMode();
    const azureStorageAccountsStoreHouse = useLocalStore<ResourceStoreHouse<AzureStorageAccountStore>>(createResourceStoreHouse);
    const storageAccountTracker = useProgressTracker();
    const rebindTracker = useProgressTracker({ persistOnComplete: true });
    const updateCsAccountTracker = useProgressTracker();
    const checkPermissionsTracker = useProgressTracker<AzurePermissionsResultSwagger>({ persistOnComplete: true });

    const refreshTokenTracker = useProgressTracker({
        onComplete: () => {
            dispatcher.dispatch('checkAndEnableStorageCORS', {
                storageAccountName,
                tracker: corsCheckTracker
            });
        }
    });
    const corsCheckTracker = useProgressTracker({ onComplete: onSubmit });

    const isStorageAccountAttached = Boolean(csAccountStore?.ownedStorageAccountInfo.name);
    const isStorageAccountAlreadyAttached = isStorageAccountAttached && rebindTracker.isNotStarted;

    const authoringResourcesOptions: IDropdownOption[] =
        (csAccountStore && [{ key: csAccountStore?.id, text: csAccountStore?.name ?? '' }]) || [];
    const notSupportedRegion =
        !LuisConstants.CustomText.PrivatePreviewAllowedRegions.some(r => r === csAccountStore?.region) ||
        LuisConstants.LanguageStudio.AllowedSkuCodes.every(sku => csAccountStore?.sku.code !== sku);
    const isValidCustomTextResource = !notSupportedRegion && csAccountStore?.isIdentityEnabled;

    const storageAccountsOptions: IDropdownOption[] = isStorageAccountAlreadyAttached
        ? [{ key: csAccountStore.ownedStorageAccountInfo.name, text: csAccountStore.ownedStorageAccountInfo.name }]
        : azureStorageAccountsStoreHouse.items.map(c => ({ key: c.name, text: c.name }));

    const isStorageAccountsDropdownDisabled =
        !subscriptionId ||
        storageAccountTracker.isRunning ||
        rebindTracker.isRunning ||
        corsCheckTracker.isRunning ||
        checkPermissionsTracker.isRunning ||
        refreshTokenTracker.isRunning ||
        isStorageAccountAttached ||
        !isValidCustomTextResource;
    const isConnectStorageButtonDisabled =
        isStorageAccountsDropdownDisabled ||
        (checkPermissionsTracker.isCompleted &&
            (!checkPermissionsTracker.context.csAccountPermissionsSatisfied ||
                !checkPermissionsTracker.context.storageAccountPermissionsSatisfied));
    const isModalLoading =
        storageAccountTracker.isRunning ||
        corsCheckTracker.isRunning ||
        rebindTracker.isRunning ||
        checkPermissionsTracker.isRunning ||
        refreshTokenTracker.isRunning;
    const isModalDisabled =
        corsCheckTracker.isRunning ||
        corsCheckTracker.isFailed ||
        rebindTracker.isRunning ||
        rebindTracker.isFailed ||
        storageAccountTracker.isRunning ||
        storageAccountTracker.isFailed ||
        checkPermissionsTracker.isRunning ||
        checkPermissionsTracker.isFailed ||
        refreshTokenTracker.isRunning ||
        refreshTokenTracker.isFailed;

    const isValid = !isModalDisabled && isStorageAccountAttached && csAccountStore?.isIdentityEnabled;

    const loadStorageAccounts = () =>
        dispatcher.dispatch('getAzureStorageAccountsBySubscriptionIdAndRegion', {
            id: subscriptionId,
            region: csAccountStore?.region,
            tracker: storageAccountTracker,
            localStore: azureStorageAccountsStoreHouse
        });

    React.useEffect(() => {
        if (subscriptionId && isValidCustomTextResource && !isStorageAccountAlreadyAttached) {
            loadStorageAccounts();
        }
    }, [subscriptionId, isValidCustomTextResource, isStorageAccountAlreadyAttached]);

    React.useEffect(() => {
        if (Boolean(storageAccountName) && !isStorageAccountAttached) {
            const selectedStorageAccount = azureStorageAccountsStoreHouse.items.find(sa => sa.name === storageAccountName);
            if (selectedStorageAccount) {
                dispatcher.dispatch('checkAzureCsAccountAndStorageRequiredRoles', {
                    id: subscriptionId,
                    csAccount: csAccountStore,
                    storageAccount: selectedStorageAccount,
                    tracker: checkPermissionsTracker
                });
            }
        }
    }, [storageAccountName]);

    const refreshCurrentSelectedResource = () => dispatcher.dispatch('updateCurrentSelectedCsAccount', { tracker: updateCsAccountTracker });

    const connectStorage = () => {
        if (Boolean(storageAccountName) && !isStorageAccountAttached) {
            const storageAccountPath = azureStorageAccountsStoreHouse.items.find(sa => sa.name === storageAccountName).path;
            dispatcher.dispatch('rebindStorageAccountToCurrentCsAccount', { storageAccountPath, tracker: rebindTracker });
        }
    };

    const onNext = () => {
        if (!isValid) {
            return;
        }

        if (isStorageAccountAlreadyAttached) {
            onSubmit();
        } else {
            const selectedStorageAccount = azureStorageAccountsStoreHouse.items.find(sa => sa.name === storageAccountName);
            if (selectedStorageAccount) {
                dispatcher.dispatch('addAzureStorageRequiredRoles', {
                    id: subscriptionId,
                    csAccount: csAccountStore,
                    storageAccount: selectedStorageAccount
                });
            }
        }
    };

    const labelOnRender = () => (
        <Stack horizontal verticalAlign="center">
            <Label styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required>
                {localize(messages.storageAccountLabel)}
            </Label>
            <CalloutButton content={localize(messages.storageAccountTooltipContent)} />
        </Stack>
    );

    const getErrorNotes = () => (
        <Stack.Item>
            <ExternalLink
                href={`https://aka.ms/${
                    isExtractionProjects ? 'ct-before-get-started-permissions' : 'ctc-before-get-started-permissions'
                }`}>
                {localize(messages.learnMore)}
            </ExternalLink>
            <br />
            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.rolesAssignmentWarning)}</LuisText>
        </Stack.Item>
    );

    const renderPermissionListItem = (
        key: number,
        permission: { name: string; icon: 'Completed' | 'Warning' | 'Info'; style: IProcessedStyleSet<IIconStyles> }
    ) => (
        <Stack.Item key={key}>
            <Icon iconName={permission.icon} styles={permission.style} />
            &nbsp;
            <LuisText>{permission.name}</LuisText>
        </Stack.Item>
    );

    const renderPermissionList = ({
        permissions
    }: {
        permissions: { name: string; icon: 'Completed' | 'Warning' | 'Info'; style: IProcessedStyleSet<IIconStyles> }[];
    }) => <Stack tokens={{ childrenGap: 15 }}>{permissions.map((p, idx) => renderPermissionListItem(idx, p))}</Stack>;

    const renderPermissionsSection = () => {
        if (!checkPermissionsTracker.isCompleted) {
            return null;
        }

        const { csAccountPermissionsSatisfied, storageAccountPermissionsSatisfied } = checkPermissionsTracker.context;
        return (
            <Stack.Item>
                <Stack horizontal verticalAlign="center">
                    <Label>{localize(messages.csAccountPermissionsLabel)}</Label>
                </Stack>

                {csAccountPermissionsSatisfied &&
                    renderPermissionList({
                        permissions: [
                            { name: localize(messages.ownerContributorRoleGrantedMessage), icon: 'Completed', style: successIconStyles }
                        ]
                    })}

                {!csAccountPermissionsSatisfied &&
                    renderPermissionList({
                        permissions: [
                            { name: localize(messages.ownerContributorRoleRequiredMessage), icon: 'Warning', style: warningIconStyles }
                        ]
                    })}

                <Stack horizontal verticalAlign="center">
                    <Label styles={{ root: { paddingTop: '10px' } }}>{localize(messages.storageAccountPermissionsLabel)}</Label>
                </Stack>

                {storageAccountPermissionsSatisfied &&
                    renderPermissionList({
                        permissions: [
                            { name: localize(messages.ownerContributorRoleGrantedMessage), icon: 'Completed', style: successIconStyles },
                            { name: localize(messages.storageBlobRolesGrantedMessage), icon: 'Info', style: pendingIconStyles }
                        ]
                    })}

                {!storageAccountPermissionsSatisfied &&
                    renderPermissionList({
                        permissions: [
                            { name: localize(messages.ownerContributorRoleRequiredMessage), icon: 'Warning', style: warningIconStyles },
                            { name: localize(messages.storageBlobRolesRequiredMessage), icon: 'Warning', style: warningIconStyles }
                        ]
                    })}

                <Stack tokens={{ padding: '30px 0 0 0' }}>
                    <ExternalLink
                        href={`https://aka.ms/${isExtractionProjects ? 'required-roles-custom-ner' : 'required-roles-classification'}`}>
                        {localize(messages.requiredRolesDetails)}
                    </ExternalLink>
                </Stack>
            </Stack.Item>
        );
    };

    return (
        <Stack>
            {rebindTracker.isCompleted && (
                <MessageBar messageBarType={MessageBarType.success} styles={connectStorageMessageBarStyles}>
                    {localize(messages.connectStorageAccountSuccessMessage)}
                </MessageBar>
            )}

            <Stack
                styles={{ root: { maxHeight: '400px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `${isLargeOrGreater ? `0 0 0 ${projectCreationContentLeftMargin}` : '12px 20px'}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    {isStorageAccountAlreadyAttached && (
                        <MessageBar messageBarType={MessageBarType.info}>{localize(messages.storageAlreadyAttachedMessage)}</MessageBar>
                    )}
                    {!isStorageAccountAlreadyAttached && <LuisText>{localize(messages.description)}</LuisText>}
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                    <Stack.Item>
                        <Stack horizontal tokens={{ childrenGap: 4 }}>
                            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.subscription)}</LuisText>
                            <LuisText>
                                &nbsp;
                                {subscriptionName}
                            </LuisText>
                        </Stack>
                    </Stack.Item>

                    <Stack.Item>
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <StyledDropdown
                                disabled
                                isLargeOrGreater={isLargeOrGreater}
                                options={authoringResourcesOptions}
                                isLoading={updateCsAccountTracker.isRunning}
                                selectedKey={csAccountStore?.id}
                                label={localize(messages.azureResourceLabel)}
                                placeholder={localize(messages.azureResourcePlaceholder)}
                            />
                            {!isStorageAccountAlreadyAttached && (
                                <LuisTooltip content={localize(messages.refreshDescription)}>
                                    <IconButton
                                        styles={refreshButtonStyles}
                                        onClick={refreshCurrentSelectedResource}
                                        iconProps={{ iconName: 'Refresh' }}
                                        ariaLabel={'Refresh'}
                                        disabled={updateCsAccountTracker.isRunning}
                                    />
                                </LuisTooltip>
                            )}
                        </Stack>
                        <LuisText variant="small">
                            <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                                {localize(messages.changeAzureResource)}
                            </Link>
                        </LuisText>
                    </Stack.Item>

                    {!isStorageAccountAlreadyAttached && (
                        <Stack tokens={{ padding: '24px 0 0 0' }}>
                            <LuisText>{localize(messages.storageDescription)}</LuisText>
                        </Stack>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <StyledDropdown
                            isLargeOrGreater={isLargeOrGreater}
                            options={storageAccountsOptions}
                            selectedKey={storageAccountName}
                            isLoading={storageAccountTracker.isRunning || corsCheckTracker.isRunning || checkPermissionsTracker.isRunning}
                            disabled={isStorageAccountsDropdownDisabled}
                            onChange={(_, o) => setStorageAccountName(o.key as string)}
                            placeholder={localize(messages.storageAccountPlaceholder)}
                            emptyStatePlaceholder={localize(messages.emptyItemPlaceholder)}
                            ariaLabel={localize(messages.storageAccountLabel)}
                            onRenderLabel={labelOnRender}
                        />
                        {!isStorageAccountAlreadyAttached && (
                            <LuisTooltip content={localize(messages.refreshDescription)}>
                                <IconButton
                                    styles={refreshButtonStyles}
                                    onClick={loadStorageAccounts}
                                    iconProps={{ iconName: 'Refresh' }}
                                    ariaLabel={'Refresh'}
                                    disabled={isStorageAccountsDropdownDisabled}
                                />
                            </LuisTooltip>
                        )}
                        {!isStorageAccountAlreadyAttached && (
                            <PrimaryButton
                                styles={connectStorageButtonStyles}
                                disabled={isConnectStorageButtonDisabled}
                                onClick={connectStorage}>
                                {isStorageAccountAttached
                                    ? localize(messages.storageAccountConnectedButtonLabel)
                                    : localize(messages.connectStorageAccountButtonLabel)}
                            </PrimaryButton>
                        )}
                    </Stack>
                </Stack>

                {renderPermissionsSection()}

                <Stack.Item>
                    {notSupportedRegion && (
                        <CollapsedErrorMessage error={new Error(localize(messages.invalidRegion))}>{getErrorNotes()}</CollapsedErrorMessage>
                    )}
                    {!csAccountStore?.isIdentityEnabled && (
                        <CollapsedErrorMessage error={new Error(localize(messages.invalidResource))}>
                            <Link
                                onClick={() =>
                                    dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: 'settings', settingsTab: 'resources' })
                                }>
                                {localize(messages.enableIdentity)}
                            </Link>
                        </CollapsedErrorMessage>
                    )}
                    {corsCheckTracker.isFailed && (
                        <CollapsedErrorMessage
                            error={corsCheckTracker.error}
                            collapsedErrorMessage={
                                corsCheckTracker.error?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                                localize(messages.enableCORSFailed, {
                                    resourceName: csAccountStore?.name,
                                    storageAccountName
                                })
                            }>
                            {getErrorNotes()}
                        </CollapsedErrorMessage>
                    )}
                    {rebindTracker.isFailed && <CollapsedErrorMessage error={rebindTracker.error}>{getErrorNotes()}</CollapsedErrorMessage>}
                    {storageAccountTracker.isFailed && (
                        <CollapsedErrorMessage error={storageAccountTracker.error}>{getErrorNotes()}</CollapsedErrorMessage>
                    )}
                    {refreshTokenTracker.isFailed && (
                        <CollapsedErrorMessage error={refreshTokenTracker.error}>{getErrorNotes()}</CollapsedErrorMessage>
                    )}
                </Stack.Item>
            </Stack>

            <ModalFooter
                onDismiss={onDismiss}
                activeStep={ProjectCreationSteps.ConnectStorage}
                onNext={onNext}
                isValid={isValid}
                isFooterDisabled={isModalDisabled}
                isLoading={isModalLoading}
            />
        </Stack>
    );
});
